import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RedeemService } from '../redeem.service';
import { SharedService } from '../../shared/shared.service';
import { LoaderService } from '../../loader.service';
import { AuthService } from '../../auth.service';
import { ToasterService } from '../../toaster.service';
import { Toaster, ToasterType } from '../../shared/types/toaster.types';
import {
  WEEK_DAYS, TRANSACTION_PERIODS, VARIABLE_LIST, OPERATOR_LIST, BOOLEAN_VALUES
} from '../redeem.types';
import { LIST_REDEEMS } from '../../routes';

@Component({
  selector: 'app-redeem-create',
  templateUrl: './redeem-create.component.html',
  styleUrls: ['./redeem-create.component.css']
})
export class RedeemCreateComponent implements OnInit {
  public actionTitle = 'Add';
  public isEdit = false;
  private ruleId: string | null = null;

  public rule: any = {};
  public cities: { id: string, name: string }[] = [];
  public modes: { id: string, name: string }[] = [];
  public shifts: string[] = [];
  public selectedCity: { id: string, name: string } | null = null;
  public selectedMode: { id: string, name: string } | null = null;
  public weekDays = WEEK_DAYS;
  public transactionPeriods = TRANSACTION_PERIODS;
  public variableList = VARIABLE_LIST;
  public operatorList = OPERATOR_LIST;
  public booleanValues = BOOLEAN_VALUES;

  public disableSubmit = false;
  public maxLimits: any = {};

  public blockRedeem = false;

  @ViewChild('startDateInput') startDateInput: ElementRef<HTMLInputElement> | null = null;

  // Auto-approval rule builder state (mirrors the legacy controller).
  public multiAutoRulesSet: any = {};
  public generatedRule: any = {};
  public setCount = 0;
  public newRuleGenerated = false;
  private tempRules: any = {};

  constructor(
    private redeemService: RedeemService,
    private sharedService: SharedService,
    private loaderService: LoaderService,
    private authService: AuthService,
    public toasterService: ToasterService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  async ngOnInit() {
    this.ruleId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.ruleId;
    this.actionTitle = this.isEdit ? 'Edit' : 'Add';

    const requiredPermission = this.isEdit ? 'redeem_edit' : 'redeem_add';
    const permissions = await this.authService.getPermissions();
    if (!permissions || permissions.indexOf(requiredPermission) < 0) {
      this.errorMessage('You are not authorized to access this page');
      this.router.navigate(['/']);
      return;
    }

    await this.withLoader(async () => {
      await this.fetchDashboardConfig();
      await this.fetchCities();
      if (this.isEdit) {
        await this.getRuleDetails(this.ruleId);
      } else {
        await this.applyLaunchQueryPrefill();
      }
    });
  }

  // Prefill create form from CSCC
  private async applyLaunchQueryPrefill() {
    const q = this.route.snapshot.queryParams || {};
    const cityId = (q['city'] || '').trim();
    const modeId = (q['mode'] || '').trim();
    if (!cityId) {
      return;
    }

    const city = this.cities.find((c) => String(c.id) === String(cityId));
    if (!city) {
      return;
    }

    this.selectedCity = city;
    await this.onCityChange(city);

    if (!modeId) {
      this.errorMessage('Mode is required in launch URL.');
      return;
    }
    const mode = this.modes.find((m) => String(m.id) === String(modeId));
    if (mode) {
      await this.onModeChange(mode);
    } else {
      this.errorMessage('Invalid mode in launch URL for the selected city.');
    }
  }

  private async withLoader(cb) {
    try {
      this.loaderService.openLoading();
      return await cb();
    } finally {
      this.loaderService.closeLoading();
    }
  }

  private async fetchDashboardConfig() {
    try {
      const res: any = await this.sharedService.fetchDashboardConfig().toPromise();
      this.maxLimits = (res && res.data && res.data.redeemRulesMaxLimits) || {};
    } catch (err) {
      this.maxLimits = {};
    }
  }

  private async fetchCities() {
    const res: any = await this.sharedService.fetchCities().toPromise();
    this.cities = (res.data.cities || [])
      .filter((c: any) => c.active)
      .map((c: any) => ({ id: c._id, name: c.displayName }));
  }

  // City -> Modes (same as the create-shift flow). Cities are stored as displayName strings.
  compareModeById = (a: any, b: any): boolean => {
    if (!a || !b) {
      return a === b;
    }
    return String(a.id) === String(b.id);
  }

  async onCityChange(city: { id: string, name: string } | null) {
    this.selectedMode = null;
    this.modes = [];
    this.shifts = [];
    this.rule.shift = [];
    this.blockRedeem = false;
    if (!city) {
      this.rule.cities = [];
      return;
    }
    this.rule.cities = [city.name];
    await this.fetchModesForCity(city.id);
  }

  private async fetchModesForCity(cityId: string) {
    if (!cityId) {
      return;
    }
    try {
      const res: any = await this.withLoader(() => this.sharedService.fetchModes(cityId).toPromise());
      this.modes = (res && res.data && res.data.modes) || [];
    } catch (err) {
      this.modes = [];
      this.errorMessage('Error fetching modes');
    }
  }

  // Mode selected -> fetch shifts for the city+mode combination from captain-earnings (/api/v1/shifts).
  async onModeChange(mode: { id: string, name: string } | null) {
    this.selectedMode = mode;
    this.shifts = [];
    this.rule.shift = [];
    this.blockRedeem = false;
    if (this.selectedCity && mode) {
      await this.fetchCaptainShifts(this.selectedCity.id, mode.id);
    }
  }

  private async fetchCaptainShifts(cityId: string, modeId: string) {
    if (!cityId || !modeId) {
      return;
    }
    try {
      const res: any = await this.withLoader(() =>
        this.sharedService.fetchCaptainEarningsShifts(cityId, modeId, false).toPromise());
      let arr: any[] = [];
      if (res && res.data && res.data.shifts && Array.isArray(res.data.shifts)) {
        arr = res.data.shifts;
      } else if (res && res.data && Array.isArray(res.data)) {
        arr = res.data;
      } else if (Array.isArray(res)) {
        arr = res;
      }
      const names = arr
        .map((s: any) => (typeof s === 'object' && s !== null) ? (s.name || '') : String(s))
        .filter((name: string) => name.trim() !== '');
      // Keep any already-selected shifts (e.g. on edit) visible in the options list.
      const merged = new Set<string>([...(this.rule.shift || []), ...names]);
      this.shifts = Array.from(merged);
    } catch (err) {
      this.errorMessage('Error fetching shifts');
    }
  }

  get isShiftScoped(): boolean {
    return !!(this.selectedCity && this.selectedMode && this.rule.shift && this.rule.shift.length > 0);
  }

  onShiftChange(): void {
    if (this.blockRedeem) {
      if (this.isShiftScoped) {
        this.rule.tnc = this.blockRedeemTnc();
      } else {
        this.blockRedeem = false;
      }
    }
  }

  onBlockRedeemChange(): void {
    if (this.blockRedeem) {
      this.applyBlockRedeemDefaults();
    }
  }

  private blockRedeemTnc(): string {
    const shiftLabel = (this.rule.shift || []).join(', ');
    return shiftLabel ? `Redeem is blocked for shift ${shiftLabel}.` : 'Redeem is blocked for this shift.';
  }

  private applyBlockRedeemDefaults(): void {
    this.rule.transactionCount = 0;
    this.rule.weekDays = [...WEEK_DAYS];
    this.rule.transactionPeriod = TRANSACTION_PERIODS[0];
    this.rule.minTransactionAmount = 1;
    this.rule.startDate = this.today;
    this.rule.firstRedeemMinAmount = undefined;
    this.rule.tnc = this.blockRedeemTnc();
    this.multiAutoRulesSet = {};
    this.setCount = 0;
  }

  openStartDatePicker(): void {
    const el = this.startDateInput ? this.startDateInput.nativeElement : null;
    if (!el) {
      return;
    }
    const input = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      el.focus();
      el.click();
    }
  }

  // ---- Auto-approval rule builder (ported verbatim from legacy) ----

  // Ordered set keys for template iteration (keys are sequential numeric strings).
  ruleSetKeys(): string[] {
    return Object.keys(this.multiAutoRulesSet).sort((a, b) => Number(a) - Number(b));
  }

  addNewRule() {
    this.multiAutoRulesSet[this.setCount] = [{ variable: null, operator: null, value: '' }];
    this.setCount++;
  }

  addRule(index) {
    const currentRule = this.multiAutoRulesSet[index];
    if (currentRule.length !== 0) {
      this.validateAndGenerateRules(index);
    }
    if (this.newRuleGenerated || currentRule.length === 0) {
      currentRule.push({ variable: null, operator: null, value: '' });
    }
  }

  deleteRuleSet(index) {
    if (this.multiAutoRulesSet[index]) {
      delete this.multiAutoRulesSet[index];
      this.setCount--;
    }
    if (this.setCount === 0) {
      this.multiAutoRulesSet = {};
    } else {
      const tempSet = {};
      let tempCount = 0;
      for (const rule in this.multiAutoRulesSet) {
        if (this.multiAutoRulesSet[rule]) {
          tempSet[tempCount] = this.multiAutoRulesSet[rule];
          tempCount++;
        }
      }
      this.multiAutoRulesSet = tempSet;
    }
  }

  removeRule(parentIndex, index) {
    if (this.multiAutoRulesSet[parentIndex] && this.multiAutoRulesSet[parentIndex].length > index) {
      const currentRuleSet = this.multiAutoRulesSet[parentIndex];
      const rules = [];
      let i = 0;
      for (const rul in currentRuleSet) {
        if (currentRuleSet.hasOwnProperty(rul)) {
          if (i !== index) {
            rules.push(currentRuleSet[rul]);
          }
          i++;
        }
      }
      if (rules.length) {
        this.multiAutoRulesSet[parentIndex] = rules;
      } else {
        this.deleteRuleSet(parentIndex);
      }
    }
  }

  changeInVariable(parentIndex, index) {
    if (this.multiAutoRulesSet[parentIndex][index].variable === 'isFirstRedeem') {
      this.multiAutoRulesSet[parentIndex][index].operator = 'boolean';
    }
  }

  private isEmpty(value) {
    return (value == null || value.length === 0);
  }

  private isInt(n) {
    return Number(n) === n && n % 1 === 0;
  }

  validateAndGenerateRules(index) {
    this.newRuleGenerated = false;
    const currentRule = this.multiAutoRulesSet[index];
    if (currentRule.length < 1) {
      this.errorMessage('You must add at least one rule for auto-approval');
    }
    let allFilled = false;
    const uniqueVariables = [];
    const variables = Object.keys(this.maxLimits);
    for (const x in currentRule) {
      if (currentRule.hasOwnProperty(x)) {
        if (uniqueVariables.indexOf(currentRule[x].variable) !== -1) {
          this.errorMessage('You can use a variable only in one rule. There are more than one rules with the variable \''
            + currentRule[x].variable + '\'');
          return;
        } else {
          uniqueVariables.push(currentRule[x].variable);
        }
        const i = parseInt(x, 10) + 1;
        if (!currentRule[x].variable) {
          this.errorMessage('Please enter a variable for rule #' + i);
          return;
        } else if (!currentRule[x].operator) {
          this.errorMessage('Please enter an operator for rule #' + i);
          return;
        } else if (this.isEmpty(currentRule[x].value)) {
          this.errorMessage('Please enter a value for rule #' + i);
          return;
        } else if (currentRule[x].value < 0) {
          this.errorMessage('Please enter a positive value for rule #' + i);
          return;
        } else if ((variables.indexOf(currentRule[x].variable) > -1 &&
          currentRule[x].value > this.maxLimits[currentRule[x].variable]) ||
          (currentRule[x].maxValue &&
            currentRule[x].maxValue > this.maxLimits[currentRule[x].variable])) {
          this.errorMessage(`Max limit for ${currentRule[x].variable} is ${this.maxLimits[currentRule[x].variable]}`);
          return;
        } else {
          allFilled = true;
        }
      }
    }
    if (allFilled) {
      this.checkAllRuleTypes(index);
    }
  }

  private checkAllRuleTypes(index) {
    const currentRule = this.multiAutoRulesSet[index];
    let checked = 0;
    for (const x in currentRule) {
      if (currentRule.hasOwnProperty(x)) {
        const i = parseInt(x, 10) + 1;
        if (currentRule[x].variable === 'redeemAmount' || currentRule[x].variable === 'totalKm') {
          if (currentRule[x].value < 1) {
            this.errorMessage('Enter a positive value (greater than 0) for rule #' + i);
            return;
          } else {
            checked++;
          }
        } else if (currentRule[x].variable === 'numberOfOrders') {
          if (!this.isInt(currentRule[x].value) || currentRule[x].value < 1) {
            this.errorMessage('Enter an integer (without decimal) value, greater than 0 for rule #' + i);
            return;
          } else {
            checked++;
          }
        } else if (currentRule[x].variable === 'isFirstRedeem') {
          if (currentRule[x].value !== 'true' && currentRule[x].value !== 'false') {
            this.errorMessage('The operator for isFirstRedeem must be T/F and must contain True or False as value');
            return;
          } else {
            checked++;
          }
        } else {
          checked++;
        }
        if (currentRule[x].operator === 'between') {
          if (this.isEmpty(currentRule[x].value) || this.isEmpty(currentRule[x].maxValue)) {
            this.errorMessage('You must enter both values for the \'between\' operator');
            checked--;
            return;
          } else if (currentRule[x].value > currentRule[x].maxValue || currentRule[x].value === currentRule[x].maxValue) {
            this.errorMessage('The min value cannot be greater than or equal to the max value for the between operator');
            checked--;
            return;
          }
        }
      }
    }
    if (checked === currentRule.length) {
      this.generateRule(index);
    } else {
      this.errorMessage('Something went wrong. Please check the rules again.');
      return;
    }
  }

  private generateRule(index) {
    const currentRule = this.multiAutoRulesSet[index];
    const tempRules = [];
    for (const x in currentRule) {
      if (currentRule[x].operator === 'between') {
        const str = currentRule[x].value + ' < ' + currentRule[x].variable + ' < ' + currentRule[x].maxValue;
        tempRules.push({ variable: currentRule[x].variable, condition: str, max: currentRule[x].maxValue, min: currentRule[x].value });
      } else if (currentRule[x].operator === 'boolean') {
        const str = currentRule[x].variable + ' == ' + currentRule[x].value;
        tempRules.push({ variable: currentRule[x].variable, condition: str });
      } else {
        const str = currentRule[x].variable + ' ' + currentRule[x].operator + ' ' + currentRule[x].value;
        tempRules.push({ variable: currentRule[x].variable, condition: str, max: currentRule[x].value, min: 0 });
      }
    }
    this.generatedRule[index] = tempRules;
    this.newRuleGenerated = true;
  }

  private generateMultiSetRules() {
    let allGood = true;
    for (const ruleSet in this.multiAutoRulesSet) {
      if (this.multiAutoRulesSet.hasOwnProperty(ruleSet)) {
        if (this.multiAutoRulesSet[ruleSet]) {
          for (const rule in this.multiAutoRulesSet[ruleSet]) {
            if (this.multiAutoRulesSet[ruleSet][rule]) {
              const currentRule = this.multiAutoRulesSet[ruleSet][rule];
              if (!currentRule.variable || !currentRule.operator || this.isEmpty(currentRule.value)) {
                this.errorMessage('Please fill all the boxes for the rules or deleted unused rules');
                allGood = false;
                break;
              }
            }
          }
        }
        if (allGood) {
          this.validateAndGenerateRules(ruleSet);
        }
      }
    }
  }

  // ---- Edit reconstruction (ported verbatim from legacy) ----

  private async getRuleDetails(id) {
    try {
      const response: any = await this.redeemService.getRule(id).toPromise();
      this.rule = response.data;
      const rawTnc = (this.rule as any).tnc;
      const tncText = Array.isArray(rawTnc) ? rawTnc[0] : rawTnc;
      if (typeof tncText === 'string' && tncText.trim() !== '') {
        this.rule.tnc = tncText.split(/\r?\n/).join(';');
      }
      // Reconstruct city, mode (if persisted) and shift options from the saved rule.
      if (this.rule.cities && this.rule.cities.length) {
        const city = this.cities.find(c => c.name === this.rule.cities[0]);
        if (city) {
          this.selectedCity = city;
          await this.fetchModesForCity(city.id);
          if (this.rule.modeId) {
            this.selectedMode = this.modes.find(m => String(m.id) === String(this.rule.modeId)) || null;
            if (this.selectedMode) {
              await this.fetchCaptainShifts(city.id, this.selectedMode.id);
            }
          }
        }
        // Seed the shift options so any saved shifts render as selected.
        this.shifts = Array.from(new Set([...(this.rule.shift || []), ...this.shifts]));
      }
      this.blockRedeem = this.isShiftScoped
        && (this.rule.blockRedeem === true || Number(this.rule.transactionCount) === 0);
      this.tempRules = this.rule.multiAutoRedeemRules ? this.rule.multiAutoRedeemRules : {};
      this.setCount = this.rule.multiAutoRedeemRules && Object.keys(this.rule.multiAutoRedeemRules)
        ? Object.keys(this.rule.multiAutoRedeemRules).length : 0;
      if (this.tempRules && Object.keys(this.tempRules).length) {
        this.getAutomationRule();
      } else {
        if (this.rule.automationRules && this.rule.automationRules.length) {
          this.tempRules = { 0: this.rule.automationRules };
          this.getAutomationRule();
          this.setCount = 1;
        } else {
          this.warningMessage('No auto-approval rules added for this rule.');
        }
      }
    } catch (err) {
      this.errorMessage(err && err.info ? err.info.message : (err && err.message) || 'Unable to fetch rule');
    }
  }

  private getAutomationRule() {
    this.multiAutoRulesSet = {};
    for (const ruleSet in this.tempRules) {
      if (this.tempRules.hasOwnProperty(ruleSet)) {
        if (this.tempRules[ruleSet]) {
          this.multiAutoRulesSet[ruleSet] = [];
          this.tempRules[ruleSet].map(rule => {
            const splitRule = rule.condition.split(' ');
            if (splitRule.length === 3) {
              if (splitRule[2] === 'true' || splitRule[2] === 'false') {
                this.multiAutoRulesSet[ruleSet].push({ variable: rule.variable, operator: 'boolean', value: splitRule[2] });
              } else {
                this.multiAutoRulesSet[ruleSet].push(
                  { variable: rule.variable, operator: splitRule[1], value: parseFloat(splitRule[2]) });
              }
            }
            if (splitRule.length === 5) {
              this.multiAutoRulesSet[ruleSet].push(
                { variable: rule.variable, operator: 'between', value: parseFloat(splitRule[0]), maxValue: parseFloat(splitRule[4]) });
            }
          });
        }
        this.validateAndGenerateRules(ruleSet);
      }
    }
  }

  // ---- Save (ported verbatim from legacy save validations) ----

  // Today's date as YYYY-MM-DD (matches the stored date format for lexical comparison).
  get today(): string {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  async save() {
    if (this.blockRedeem && !this.isShiftScoped) {
      this.errorMessage('Block Redeem requires a city, mode and at least one shift');
      return;
    }
    if (this.blockRedeem) {
      this.applyBlockRedeemDefaults();
    }

    // City is required. Mode and Shift are optional (city-wide / city+mode / city+mode+shift).
    if (!this.rule.cities || !Array.isArray(this.rule.cities) || this.rule.cities.length === 0) {
      this.errorMessage('Must select at least one city');
      return;
    }
    if (!this.rule.weekDays || !Array.isArray(this.rule.weekDays) || this.rule.weekDays.length === 0) {
      this.errorMessage('Must select weekday(s)');
      return;
    }
    if (!this.rule.transactionPeriod) {
      this.errorMessage('Must select a type');
      return;
    }
    if (!this.blockRedeem) {
      if (!this.rule.transactionCount) {
        this.errorMessage('Please enter No. of transactions');
        return;
      }
      if (isNaN(this.rule.transactionCount) || this.rule.transactionCount < 1) {
        this.errorMessage('No. of transactions must be a positive number (greater than 0)');
        return;
      }
    }
    if (!this.rule.minTransactionAmount) {
      this.errorMessage('Please enter a minimum transaction amount');
      return;
    }
    if (isNaN(this.rule.minTransactionAmount) || this.rule.minTransactionAmount < 1) {
      this.errorMessage('Minimum transaction amount must be a positive number (greater than 0)');
      return;
    }
    if (!this.rule.startDate) {
      this.errorMessage('Must enter a start date');
      return;
    }
    if (this.rule.startDate < this.today) {
      this.errorMessage('Start date cannot be earlier than today');
      return;
    }
    if (!this.rule.tnc) {
      this.errorMessage('Please mention the terms and conditions');
      return;
    }

    this.generatedRule = {};
    if (this.blockRedeem) {
      this.newRuleGenerated = true;
    } else if (this.multiAutoRulesSet && Object.keys(this.multiAutoRulesSet).length) {
      this.generateMultiSetRules();
    } else {
      this.errorMessage('Please add at least one auto-approval rule');
      return;
    }

    if (!this.newRuleGenerated) {
      return;
    }

    // Build payload (tnc converted to newline-joined; backend splits on \n).
    const payload: any = { ...this.rule };
    payload.tnc = (this.rule.tnc + '').replace(/;/g, '\n');
    payload.multiAutoRedeemRules = this.generatedRule;
    payload.blockRedeem = this.blockRedeem;

    // endDate is no longer part of redeem rules; never persist it (incl. when editing legacy rules).
    delete payload.endDate;

    // Rule scope, consumed by rapido-redeem's city+shift -> city+mode -> city fallback:
    //  - shift(s) selected      -> city+shift rule: persist shift; retain modeId (a mode is
    //                              always selected to load the shift list) so the rule stays
    //                              associated with its mode
    //  - mode selected, no shift -> city+mode rule: persist modeId, empty shift
    //  - neither                -> city-only rule: empty shift, no modeId
    const selectedShifts = this.rule.shift || [];
    if (selectedShifts.length > 0) {
      payload.shift = selectedShifts;
      if (this.selectedMode) {
        payload.modeId = this.selectedMode.id;
      } else {
        delete payload.modeId;
      }
    } else if (this.selectedMode) {
      payload.shift = [];
      payload.modeId = this.selectedMode.id;
    } else {
      payload.shift = [];
      delete payload.modeId;
    }

    this.disableSubmit = true;
    const request = this.isEdit
      ? this.redeemService.updateRule(this.ruleId, payload)
      : this.redeemService.createRule(payload);

    try {
      await request.toPromise();
      this.successMessage(this.isEdit ? 'Rule edited successfully.' : 'Rule added successfully.');
      this.router.navigate(['/' + LIST_REDEEMS]);
    } catch (err) {
      this.errorMessage(this.isEdit ? 'Unable to process.' : 'Unable to create rule.');
      this.disableSubmit = false;
    }
  }

  // ---- Toast helpers ----

  private errorMessage(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message }));
  }

  private successMessage(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.SUCCESS, message }));
  }

  private warningMessage(message: string) {
    this.toasterService.showToaster(new Toaster({ type: ToasterType.WARNING, message }));
  }
}
